import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { BellOff, RotateCcw, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface RtdOptOutProps {
  locationId: string | undefined;
  taskIds: string[];
  stageName: string;        // e.g. "Reviews", "Social Fortress"
  warningTitle?: string;    // dialog title
  warningBody: React.ReactNode; // dialog explanation
  size?: "sm" | "default";
  className?: string;
}

/**
 * Reusable "Not Interested" opt-out button for road-to-domination stages.
 * Marks the given task IDs as manually completed via /api/locations/:id/rtd-manual-task
 * so downstream stages unlock. User can flip back any time via "Re-enable".
 */
export function RtdOptOut({
  locationId,
  taskIds,
  stageName,
  warningTitle,
  warningBody,
  size = "sm",
  className,
}: RtdOptOutProps) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);

  const { data: rtdProgress } = useQuery<{ manualCompletedTasks?: string[] }>({
    queryKey: ["/api/locations", locationId, "rtd-progress"],
    queryFn: async () => {
      const res = await fetch(`/api/locations/${locationId}/rtd-progress`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to fetch progress");
      return res.json();
    },
    enabled: !!locationId,
  });

  const manualTasks = new Set(rtdProgress?.manualCompletedTasks ?? []);
  const isOptedOut = taskIds.length > 0 && taskIds.every(t => manualTasks.has(t));

  const mutation = useMutation({
    mutationFn: async (completed: boolean) => {
      if (!locationId) throw new Error("No location selected");
      await Promise.all(
        taskIds.map(taskId =>
          apiRequest("POST", `/api/locations/${locationId}/rtd-manual-task`, { taskId, completed }),
        ),
      );
      return { completed };
    },
    onSuccess: ({ completed }) => {
      // Refetch progress so any other surfaces (sidebar locks, RTD page) update
      // immediately. Also refetch in case of partial failure.
      queryClient.invalidateQueries({ queryKey: ["/api/locations", locationId, "rtd-progress"] });
      setOpen(false);
      toast({
        title: completed ? `${stageName} marked as 'Not Interested'` : `${stageName} re-enabled`,
        description: completed
          ? "Next stages unlocked. You can come back any time and complete this step."
          : `${stageName} is active again. Complete this step to keep building your ranking signals.`,
      });
    },
    onError: (err: Error) => {
      // Refetch to reconcile any partial state from the parallel POSTs
      queryClient.invalidateQueries({ queryKey: ["/api/locations", locationId, "rtd-progress"] });
      toast({ title: "Could not update", description: err.message, variant: "destructive" });
    },
  });

  if (!locationId) return null;

  if (isOptedOut) {
    return (
      <Button
        variant="outline"
        size={size}
        className={`text-xs ${className ?? ""}`}
        onClick={() => mutation.mutate(false)}
        disabled={mutation.isPending}
        data-testid={`button-reenable-${stageName.toLowerCase().replace(/\s+/g, "-")}`}
      >
        {mutation.isPending ? (
          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
        ) : (
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
        )}
        Re-enable {stageName}
      </Button>
    );
  }

  return (
    <>
      <Button
        variant="ghost"
        size={size}
        className={`text-xs text-muted-foreground hover:text-foreground ${className ?? ""}`}
        onClick={() => setOpen(true)}
        data-testid={`button-not-interested-${stageName.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <BellOff className="w-3.5 h-3.5 mr-1.5" />
        Not Interested
      </Button>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid={`dialog-not-interested-${stageName.toLowerCase().replace(/\s+/g, "-")}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{warningTitle ?? `Skip ${stageName} for now?`}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm">
                {warningBody}
                <div
                  className="rounded-md border border-yellow-500/30 bg-yellow-500/5 px-3 py-2 text-xs text-yellow-200"
                  data-testid="warning-tried-and-true"
                >
                  <span className="font-semibold">Heads up:</span> these recommendations are based
                  on tried-and-true data from what actually moves the needle in local search — but
                  we understand there's always an exception. If this step genuinely doesn't fit
                  your business, skipping it is fine.
                </div>
                <p>
                  If you'd rather skip this for now, no problem — we'll unlock the next stages so
                  you can keep moving. <span className="font-medium text-foreground">You can come back any time</span>{" "}
                  and click <span className="font-medium text-foreground">Re-enable {stageName}</span>.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="button-cancel-optout">Keep {stageName}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                mutation.mutate(true);
              }}
              disabled={mutation.isPending}
              data-testid="button-confirm-optout"
            >
              {mutation.isPending ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              Yes, Not Interested
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/**
 * Optional banner shown when a stage is opted out, for pages that want to
 * surface the state visibly. Lightweight wrapper around the same query.
 */
export function RtdOptOutBanner({
  locationId,
  taskIds,
  stageName,
}: {
  locationId: string | undefined;
  taskIds: string[];
  stageName: string;
}) {
  const { data: rtdProgress } = useQuery<{ manualCompletedTasks?: string[] }>({
    queryKey: ["/api/locations", locationId, "rtd-progress"],
    enabled: !!locationId,
  });

  const manualTasks = new Set(rtdProgress?.manualCompletedTasks ?? []);
  const isOptedOut = taskIds.length > 0 && taskIds.every(t => manualTasks.has(t));

  if (!isOptedOut) return null;

  return (
    <div
      className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 px-4 py-3 flex items-start gap-3"
      data-testid={`banner-opted-out-${stageName.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <BellOff className="w-4 h-4 text-yellow-400 mt-0.5 shrink-0" />
      <div className="flex-1">
        <p className="text-sm font-medium text-yellow-300">You've opted out of {stageName}</p>
        <p className="text-xs text-muted-foreground mt-0.5">
          Downstream stages are unlocked. Complete this step any time to start sending the signal.
        </p>
      </div>
    </div>
  );
}
